import React, { useState } from 'react';
import { selfAssignToJob, removeSelfFromJob } from '../services/api';

function SelfAssignButton({ job, teamMemberId, onAssigned }) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const isAlreadyAssigned = job.team_members?.some(tm => tm.id === teamMemberId);

  const handleSelfAssign = async () => {
    if (isAlreadyAssigned) {
      setMessage('You are already assigned to this job');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await selfAssignToJob(job.id, teamMemberId);
      
      setMessage(response.data.message);
      
      if (onAssigned) {
        onAssigned(response.data.job);
      }
    } catch (err) {
      console.error('Error self-assigning to job:', err);
      setError('Failed to assign to job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSelf = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await removeSelfFromJob(job.id);
      
      setMessage(response.data.message);
      
      if (onAssigned) {
        onAssigned(response.data.job);
      }
    } catch (err) {
      console.error('Error removing from job:', err);
      setError('Failed to remove from job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="self-assign-container">
      {!isAlreadyAssigned ? (
        <button
          onClick={handleSelfAssign}
          disabled={loading}
          className="btn btn-primary self-assign-btn"
        >
          {loading ? 'Adding...' : 'Add Me to This Job'}
        </button>
      ) : (
        <>
          <div className="already-assigned">
            ✓ You are assigned to this job
          </div>
          <button
            onClick={handleRemoveSelf}
            disabled={loading}
            className="btn btn-secondary remove-btn"
          >
            {loading ? 'Removing...' : 'Remove Me from This Job'}
          </button>
        </>
      )}
      
      {message && <div className="self-assign-message success">{message}</div>}
      {error && <div className="self-assign-message error">{error}</div>}

      <style jsx>{`
        .self-assign-container {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .self-assign-btn {
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          font-weight: 500;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          background-color: #2196f3;
          color: white;
        }

        .self-assign-btn:hover:not(:disabled) {
          background-color: #1976d2;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        .self-assign-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .remove-btn {
          padding: 0.3rem 0.7rem;
          font-size: 0.78rem;
          font-weight: 500;
          border: 1px solid #ccc;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          background-color: #f5f5f5;
          color: #666;
        }

        .remove-btn:hover:not(:disabled) {
          background-color: #ffebee;
          border-color: #f44336;
          color: #c62828;
        }

        .remove-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .already-assigned {
          padding: 0.3rem 0.6rem;
          background-color: #e8f5e9;
          color: #2e7d32;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.8rem;
          text-align: center;
        }

        .self-assign-message {
          padding: 0.5rem 1rem;
          border-radius: 4px;
          font-size: 0.9rem;
          text-align: center;
        }

        .self-assign-message.success {
          background-color: #e8f5e9;
          color: #2e7d32;
          border: 1px solid #4caf50;
        }

        .self-assign-message.error {
          background-color: #ffebee;
          color: #c62828;
          border: 1px solid #f44336;
        }
      `}</style>
    </div>
  );
}

export default SelfAssignButton;